import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Circle,
  Divider,
  Flex,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Skeleton,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
  VStack,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import {
  AddIcon,
  ChevronLeftIcon,
  DeleteIcon,
  EmailIcon,
  EditIcon,
  RepeatIcon,
  SearchIcon,
  SettingsIcon,
} from "@chakra-ui/icons";
import {
  FaBold,
  FaEraser,
  FaItalic,
  FaLink,
  FaListOl,
  FaListUl,
  FaQuoteRight,
  FaRedo,
  FaStrikethrough,
  FaUnderline,
  FaUndo,
  FaUnlink,
} from "react-icons/fa";
import adminApi from "../../connection/adminApi";
import AdminLayout from "./AdminLayout";

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleString(undefined, {
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const errorText = (error, fallback) =>
  error?.response?.data?.message || fallback;

const escapeHtml = (value) =>
  (value ?? "").replace(
    /[&<>"]/g,
    (character) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[character])
  );

/** Block-aware fallback: what goes in the text/plain part of what we send. */
const htmlToText = (html) =>
  (html ?? "")
    .replace(/<\s*(script|style)\b[\s\S]*?<\s*\/\s*\1\s*>/gi, "")
    .replace(/<\s*br\s*\/?>/gi, "\n")
    .replace(/<\/\s*(p|div|tr|li|h[1-6]|blockquote)\s*>/gi, "\n")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

/** "On <date>, <someone> wrote:" — the attribution above a quoted reply. */
const attribution = (message) =>
  `On ${formatDate(message.occurredAt)}, ${escapeHtml(
    message.fromName || message.fromEmail
  )} wrote:`;

/** Anything active is stripped before the mail goes into the frame. The frame
 *  itself withholds allow-scripts, so this is only belt and braces — but it
 *  keeps a stray <script> out of the document even if that ever changes. */
const stripActive = (html) =>
  html
    .replace(
      /<\s*(script|iframe|object|embed|applet)\b[\s\S]*?<\s*\/\s*\1\s*>/gi,
      ""
    )
    .replace(/<\s*(script|iframe|object|embed|applet|link|meta)\b[^>]*>/gi, "")
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
    .replace(/javascript:/gi, "");

/** Pasted mail also loses its <style> blocks: the editor is not sandboxed, so
 *  a foreign stylesheet there would restyle the console around it. */
const stripForEditor = (html) =>
  stripActive(html).replace(
    /<\s*style\b[\s\S]*?<\s*\/\s*style\s*>/gi,
    ""
  );

const frameDocument = (html) => `<!doctype html>
<html><head><meta charset="utf-8"><base target="_blank">
<style>
  html,body{margin:0;padding:0}
  body{font:14px/1.5 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;color:#2D3748;word-break:break-word}
  img{max-width:100%;height:auto}
  table{max-width:100%}
  a{color:#3182CE}
</style></head><body>${stripActive(html)}</body></html>`;

/** Mail HTML is foreign markup: it renders in its own frame so its CSS cannot
 *  reach the console, and without allow-scripts so nothing in it runs. */
const HtmlBody = ({ html }) => {
  const frame = useRef(null);
  const [height, setHeight] = useState(160);

  const fit = useCallback(() => {
    const document_ = frame.current?.contentDocument;

    if (document_?.documentElement) {
      setHeight(document_.documentElement.scrollHeight + 8);
    }
  }, []);

  useEffect(() => {
    // Remote images land after load, so measure once more when they settle.
    const timer = setTimeout(fit, 500);
    window.addEventListener("resize", fit);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", fit);
    };
  }, [fit, html]);

  return (
    <Box
      as="iframe"
      ref={frame}
      title="Message body"
      srcDoc={frameDocument(html)}
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      onLoad={fit}
      display="block"
      w="100%"
      h={`${height}px`}
      border="0"
      mt={2}
    />
  );
};

/** HTML when the mail has it, with a switch down to the plain-text part. */
const MessageBody = ({ message }) => {
  const [asHtml, setAsHtml] = useState(Boolean(message.htmlBody));

  const plain = message.textBody || htmlToText(message.htmlBody);

  return (
    <Box>
      {asHtml && message.htmlBody ? (
        <HtmlBody html={message.htmlBody} />
      ) : (
        <Text fontSize="sm" whiteSpace="pre-wrap" color="gray.700" mt={2}>
          {plain || "(empty)"}
        </Text>
      )}

      {Boolean(message.htmlBody) && Boolean(message.textBody) && (
        <Button
          mt={1}
          size="xs"
          variant="link"
          colorScheme="gray"
          onClick={() => setAsHtml((current) => !current)}
        >
          {asHtml ? "View plain text" : "View HTML"}
        </Button>
      )}
    </Box>
  );
};

/** formatBlock takes a tag name, and Firefox insists on the angle brackets. */
const BLOCKS = [
  { value: "p", label: "Normal text" },
  { value: "h1", label: "Heading 1" },
  { value: "h2", label: "Heading 2" },
  { value: "h3", label: "Heading 3" },
  { value: "pre", label: "Preformatted" },
];

/**
 * A contentEditable composer. document.execCommand is deprecated but is still
 * what every browser implements, and it costs nothing — storak-auth-ui's build
 * would otherwise have to carry a whole editor package for six commands.
 */
const RichTextEditor = ({ value, onChange, minHeight = "240px" }) => {
  const editor = useRef(null);
  // What we last handed upward. Writing innerHTML on every keystroke would
  // reset the caret to the start, so the field is uncontrolled in practice and
  // only re-syncs when the value changes from somewhere else (reply, discard).
  const emitted = useRef(value);
  const [block, setBlock] = useState("p");

  useEffect(() => {
    if (editor.current && value !== emitted.current) {
      editor.current.innerHTML = value || "";
      emitted.current = value;
    }
  }, [value]);

  const publish = useCallback(() => {
    const next = editor.current?.innerHTML ?? "";
    emitted.current = next;
    onChange(next);
  }, [onChange]);

  const exec = (command, argument) => {
    editor.current?.focus();
    document.execCommand(command, false, argument);
    publish();
  };

  const addLink = () => {
    const selection = window.getSelection()?.toString();
    // window.prompt keeps the DOM selection alive; a focused input would not.
    const url = window.prompt(
      "Link address",
      selection?.startsWith("http") ? selection : "https://"
    );

    if (url) {
      exec("createLink", url);
    }
  };

  const button = (label, icon, onClick) => (
    <Tooltip key={label} label={label} openDelay={400}>
      <IconButton
        size="xs"
        variant="ghost"
        aria-label={label}
        icon={icon}
        // Keeps focus, and the selection with it, inside the editor.
        onMouseDown={(event) => event.preventDefault()}
        onClick={onClick}
      />
    </Tooltip>
  );

  return (
    <Box borderWidth="1px" borderColor="gray.200" borderRadius="md">
      <Flex
        align="center"
        gap={0.5}
        wrap="wrap"
        px={1}
        py={1}
        borderBottomWidth="1px"
        borderColor="gray.200"
        bg="gray.50"
        borderTopRadius="md"
      >
        <Select
          size="xs"
          variant="unstyled"
          w="120px"
          mr={1}
          value={block}
          onMouseDown={(event) => event.stopPropagation()}
          onChange={(event) => {
            setBlock(event.target.value);
            exec("formatBlock", `<${event.target.value}>`);
          }}
        >
          {BLOCKS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>

        {button("Bold — Ctrl+B", <FaBold />, () => exec("bold"))}
        {button("Italic — Ctrl+I", <FaItalic />, () => exec("italic"))}
        {button("Underline — Ctrl+U", <FaUnderline />, () => exec("underline"))}
        {button("Strikethrough", <FaStrikethrough />, () =>
          exec("strikeThrough")
        )}

        <Divider orientation="vertical" h="18px" mx={1} />

        {button("Bulleted list", <FaListUl />, () =>
          exec("insertUnorderedList")
        )}
        {button("Numbered list", <FaListOl />, () => exec("insertOrderedList"))}
        {button("Quote", <FaQuoteRight />, () => exec("formatBlock", "<blockquote>"))}

        <Divider orientation="vertical" h="18px" mx={1} />

        {button("Add link", <FaLink />, addLink)}
        {button("Remove link", <FaUnlink />, () => exec("unlink"))}
        {button("Clear formatting", <FaEraser />, () => exec("removeFormat"))}

        <Box flex="1" />

        {button("Undo — Ctrl+Z", <FaUndo />, () => exec("undo"))}
        {button("Redo — Ctrl+Shift+Z", <FaRedo />, () => exec("redo"))}
      </Flex>

      <Box
        ref={editor}
        contentEditable
        suppressContentEditableWarning
        onInput={publish}
        onBlur={publish}
        onPaste={(event) => {
          // Mail pasted from another client arrives as markup; take it, minus
          // anything active, rather than flattening it to plain text.
          const html = event.clipboardData.getData("text/html");
          const text = event.clipboardData.getData("text/plain");
          event.preventDefault();

          if (html) {
            document.execCommand("insertHTML", false, stripForEditor(html));
          } else {
            document.execCommand("insertText", false, text);
          }

          publish();
        }}
        px={3}
        py={2}
        minH={minHeight}
        maxH="60vh"
        overflowY="auto"
        fontSize="sm"
        lineHeight="1.6"
        borderBottomRadius="md"
        _focusVisible={{ outline: "none", boxShadow: "0 0 0 2px #3182CE inset" }}
        sx={{
          "&:empty::before": {
            content: '"Write your message…"',
            color: "gray.400",
          },
          "& ul, & ol": { paddingInlineStart: "1.5rem" },
          "& blockquote": {
            borderLeftWidth: "2px",
            borderColor: "gray.300",
            paddingLeft: 3,
            color: "gray.600",
            marginY: 2,
          },
          "& h1": { fontSize: "xl", fontWeight: "700", marginY: 2 },
          "& h2": { fontSize: "lg", fontWeight: "700", marginY: 2 },
          "& h3": { fontSize: "md", fontWeight: "600", marginY: 2 },
          "& pre": {
            fontFamily: "mono",
            bg: "gray.50",
            p: 2,
            borderRadius: "md",
            whiteSpace: "pre-wrap",
          },
          "& a": { color: "blue.500", textDecoration: "underline" },
          "& img": { maxWidth: "100%" },
        }}
      />
    </Box>
  );
};
/* ── Per-mailbox state ─────────────────────────────────────────────────────
 * The console holds several storkplus.com addresses, and the mistake that
 * matters here is answering from the wrong one. So the page is split by
 * mailbox: one address is selected at a time, its inbox and sent live under
 * it, and the composer sends as that address. The choice, the open tab and an
 * unfinished draft survive a reload, per mailbox.
 */
const ACCOUNT_KEY = "storkplus.admin.mail.account";
const boxKey = (email) => `storkplus.admin.mail.box:${email}`;
const draftKey = (email) => `storkplus.admin.mail.draft:${email}`;

const readStored = (key) => {
  try {
    return localStorage.getItem(key) || "";
  } catch {
    return "";
  }
};

const writeStored = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* Private browsing, or a full quota — the page works without it. */
  }
};

const dropStored = (key) => {
  try {
    localStorage.removeItem(key);
  } catch {
    /* as above */
  }
};

const emptyForm = (from) => ({
  from: from || "",
  to: "",
  cc: "",
  subject: "",
  html: "",
  quoted: null,
  replyToMessageId: null,
});

const isBlankForm = (form) =>
  !form.to.trim() &&
  !form.cc.trim() &&
  !form.subject.trim() &&
  !htmlToText(form.html) &&
  !form.quoted;

const readDraft = (email) => {
  try {
    const stored = JSON.parse(readStored(draftKey(email)) || "null");

    return stored ? { ...emptyForm(email), ...stored, from: email } : null;
  } catch {
    return null;
  }
};

/** A colour per mailbox, so "which address am I in" is answerable at a glance. */
const PALETTE = [
  "blue",
  "purple",
  "teal",
  "orange",
  "pink",
  "green",
  "cyan",
  "red",
];

/**
 * Colours are handed out by order of creation rather than hashed off the
 * address: a hash collides, and two mailboxes wearing the same colour is
 * exactly the confusion this is here to prevent. Oldest mailbox keeps its
 * colour when a new one is added, because the order it indexes never changes.
 */
const paletteByEmail = (addresses) =>
  [...addresses]
    .sort((left, right) => left.id - right.id)
    .reduce((colors, address, index) => {
      colors[address.email] = PALETTE[index % PALETTE.length];

      return colors;
    }, {});

const initials = (address) => {
  const source = address?.label || address?.email?.split("@")[0] || "?";

  return source
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");
};

const AdminMail = () => {
  const toast = useToast();

  const [addresses, setAddresses] = useState([]);
  const [account, setAccount] = useState(() => readStored(ACCOUNT_KEY));
  const [box, setBox] = useState("inbox");
  const [search, setSearch] = useState("");
  const [list, setList] = useState(null);
  const [unreadByAddress, setUnreadByAddress] = useState({});
  const [listLoading, setListLoading] = useState(true);
  const [thread, setThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const addressModal = useDisclosure();
  const confirmModal = useDisclosure();
  const [newAddress, setNewAddress] = useState({ email: "", label: "" });
  const [addingAddress, setAddingAddress] = useState(false);

  const [form, setForm] = useState(() => emptyForm(readStored(ACCOUNT_KEY)));
  const [showQuoted, setShowQuoted] = useState(false);
  // The From select is hidden behind a toggle: the mailbox in the rail decides
  // it, and opening this is the deliberate act of sending as someone else.
  const [overrideFrom, setOverrideFrom] = useState(false);

  const current = useMemo(
    () => addresses.find((address) => address.email === account) || null,
    [addresses, account]
  );
  const colors = useMemo(() => paletteByEmail(addresses), [addresses]);
  const colorFor = useCallback(
    (email) => colors[email] || "gray",
    [colors]
  );
  const tone = colorFor(account);
  const mismatch = Boolean(form.from) && form.from !== account;

  const loadUnread = useCallback(async () => {
    try {
      const { data } = await adminApi.get("api/admin/mail/unread");
      setUnreadByAddress(data || {});
    } catch {
      /* The badges are a nicety; a failure here must not raise a toast. */
    }
  }, []);

  const loadAddresses = useCallback(async () => {
    try {
      const { data } = await adminApi.get("api/admin/mail/addresses");
      setAddresses(data);

      // A stored mailbox that has since been removed falls back to the default.
      setAccount((chosen) => {
        if (chosen && data.some((address) => address.email === chosen)) {
          return chosen;
        }

        return (data.find((address) => address.isDefault) || data[0])?.email || "";
      });
    } catch (error) {
      toast({
        status: "error",
        title: "Could not load the mailboxes",
        description: errorText(error),
      });
    }
  }, [toast]);

  const loadList = useCallback(async () => {
    if (!account) {
      return;
    }

    setListLoading(true);

    try {
      const { data } = await adminApi.get("api/admin/mail/messages", {
        params: {
          box,
          address: account,
          search: search || undefined,
          limit: 50,
        },
      });
      setList(data);

      if (data.unreadByAddress) {
        setUnreadByAddress(data.unreadByAddress);
      }
    } catch (error) {
      setList({ items: [], total: 0, unread: 0 });
      toast({
        status: "error",
        title: "Could not load mail",
        description: errorText(error),
      });
    } finally {
      setListLoading(false);
    }
  }, [account, box, search, toast]);

  useEffect(() => {
    loadAddresses();
    loadUnread();
  }, [loadAddresses, loadUnread]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  // The selected mailbox, its tab and its draft are remembered across reloads.
  useEffect(() => {
    if (account) {
      writeStored(ACCOUNT_KEY, account);
    }
  }, [account]);

  useEffect(() => {
    if (account) {
      writeStored(boxKey(account), box);
    }
  }, [account, box]);

  useEffect(() => {
    if (!account) {
      return;
    }

    if (isBlankForm(form)) {
      dropStored(draftKey(account));
    } else {
      writeStored(draftKey(account), JSON.stringify(form));
    }
  }, [account, form]);

  /** Switching mailbox parks the draft under the old address and opens the
   *  one waiting under the new one, so neither is lost mid-sentence. */
  const selectAccount = (email) => {
    if (email === account) {
      return;
    }

    setAccount(email);
    setForm(readDraft(email) || emptyForm(email));
    setOverrideFrom(false);
    setShowQuoted(false);
    setThread(null);
    setSearch("");
    setBox(readStored(boxKey(email)) === "sent" ? "sent" : "inbox");
  };

  // Once the addresses arrive, adopt the draft stored for the chosen mailbox.
  const hydrated = useRef(false);

  useEffect(() => {
    if (!account || hydrated.current) {
      return;
    }

    hydrated.current = true;
    setForm(readDraft(account) || emptyForm(account));
    setBox(readStored(boxKey(account)) === "sent" ? "sent" : "inbox");
  }, [account]);

  const openThread = async (threadKey) => {
    setThreadLoading(true);

    try {
      const { data } = await adminApi.get(
        `api/admin/mail/threads/${threadKey}`
      );
      setThread(data);
      // Opening marks inbound mail read, so the unread counts have moved.
      loadList();
    } catch (error) {
      toast({
        status: "error",
        title: "Could not open the conversation",
        description: errorText(error),
      });
    } finally {
      setThreadLoading(false);
    }
  };

  const startReply = (message) => {
    const subject = /^re:/i.test(message.subject)
      ? message.subject
      : `Re: ${message.subject}`;

    // A reply always goes out as the mailbox it arrived in; if that is not the
    // open one, move there too rather than replying from under another name.
    if (message.accountEmail && message.accountEmail !== account) {
      setAccount(message.accountEmail);
    }

    setForm({
      from: message.accountEmail || account,
      to:
        message.direction === "inbound" ? message.fromEmail : message.to.join(", "),
      cc: "",
      subject,
      // Empty: the quoted original sits below the editor rather than filling
      // it, so the composer stays the size of what is actually being written.
      html: "",
      quoted: {
        attribution: attribution(message),
        html: message.htmlBody || null,
        text: message.textBody || htmlToText(message.htmlBody),
      },
      replyToMessageId: message.id,
    });

    setOverrideFrom(false);
    setShowQuoted(false);

    document
      .getElementById("compose-panel")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const resetForm = () => {
    setForm(emptyForm(account));
    setOverrideFrom(false);
    setShowQuoted(false);
    dropStored(draftKey(account));
  };

  /** The reply, then the attribution, then the original inside a blockquote. */
  const composeHtml = () => {
    if (!form.quoted) {
      return form.html;
    }

    const body = form.quoted.html
      ? stripActive(form.quoted.html)
      : `<div style="white-space:pre-wrap">${escapeHtml(
          form.quoted.text
        )}</div>`;

    return `${form.html}<br><div style="color:#5f6368;font-size:13px">${form.quoted.attribution}</div><blockquote style="margin:0 0 0 0.8ex;border-left:2px solid #d0d7de;padding-left:1ex;color:#5f6368">${body}</blockquote>`;
  };

  const send = async () => {
    setSending(true);

    try {
      const html = composeHtml();

      await adminApi.post("api/admin/mail/send", {
        from: form.from,
        to: form.to,
        cc: form.cc || undefined,
        subject: form.subject,
        // Both parts go out: clients that refuse HTML still get the message.
        text: htmlToText(html),
        html,
        replyToMessageId: form.replyToMessageId || undefined,
      });

      confirmModal.onClose();

      toast({
        status: "success",
        duration: 6000,
        title: `Sent as ${form.from}`,
        description: `${form.subject} → ${form.to}`,
      });

      const wasReply = form.replyToMessageId;
      resetForm();
      loadList();
      loadUnread();

      if (wasReply && thread) {
        openThread(thread.threadKey);
      }
    } catch (error) {
      toast({
        status: "error",
        duration: 12000,
        isClosable: true,
        title: "Could not send",
        description: errorText(error, "The mail was not sent."),
      });
    } finally {
      setSending(false);
    }
  };

  const addAddress = async () => {
    setAddingAddress(true);

    try {
      await adminApi.post("api/admin/mail/addresses", newAddress);
      setNewAddress({ email: "", label: "" });
      addressModal.onClose();
      await loadAddresses();

      toast({ status: "success", title: "Mailbox added" });
    } catch (error) {
      toast({
        status: "error",
        duration: 10000,
        isClosable: true,
        title: "Could not add the mailbox",
        description: errorText(error),
      });
    } finally {
      setAddingAddress(false);
    }
  };

  const removeAddress = async (address) => {
    try {
      await adminApi.delete(`api/admin/mail/addresses/${address.id}`);
      dropStored(draftKey(address.email));
      await loadAddresses();

      if (address.email === account) {
        setAccount("");
      }

      toast({ status: "info", title: `${address.email} removed` });
    } catch (error) {
      toast({
        status: "error",
        duration: 10000,
        title: "Could not remove the mailbox",
        description: errorText(error),
      });
    }
  };

  const canSend = useMemo(
    () =>
      Boolean(form.from) &&
      Boolean(form.to.trim()) &&
      Boolean(form.subject.trim()) &&
      // An "empty" editor still holds a <br>, so judge it on its text.
      Boolean(htmlToText(form.html)),
    [form]
  );

  const totalUnread = Object.values(unreadByAddress).reduce(
    (sum, count) => sum + count,
    0
  );

  return (
    <AdminLayout>
      <Flex align="center" gap={3} mb={4} wrap="wrap">
        <Box>
          <Heading size="md">Mail</Heading>
          <Text fontSize="sm" color="gray.500">
            One mailbox at a time — what you read and what you send are the same
            address.
          </Text>
        </Box>

        <Box flex="1" />

        {totalUnread > 0 && (
          <Badge colorScheme="red" fontSize="sm" px={2} py={1} borderRadius="md">
            {totalUnread} unread
          </Badge>
        )}

        <Button
          size="sm"
          variant="outline"
          leftIcon={<SettingsIcon />}
          onClick={addressModal.onOpen}
        >
          Mailboxes
        </Button>
        <IconButton
          size="sm"
          variant="ghost"
          aria-label="Refresh"
          icon={<RepeatIcon />}
          isLoading={listLoading}
          onClick={() => {
            loadList();
            loadUnread();
          }}
          fontSize="lg"
        />
      </Flex>

      <Flex gap={4} align="flex-start" direction={{ base: "column", lg: "row" }}>
        {/* ── Mailboxes ───────────────────────────────────────────────── */}
        <Box
          bg="white"
          borderRadius="lg"
          boxShadow="sm"
          p={2}
          w={{ base: "100%", lg: "240px" }}
          flexShrink={0}
        >
          <Text
            fontSize="xs"
            fontWeight="700"
            color="gray.500"
            textTransform="uppercase"
            letterSpacing="wide"
            px={2}
            py={2}
          >
            Mailboxes
          </Text>

          <VStack align="stretch" spacing={1}>
            {addresses.map((address) => {
              const selected = address.email === account;
              const color = colorFor(address.email);
              const unread = unreadByAddress[address.email] || 0;
              const draft = Boolean(readStored(draftKey(address.email)));

              return (
                <Flex
                  key={address.id}
                  align="center"
                  gap={2}
                  px={2}
                  py={2}
                  borderRadius="md"
                  cursor="pointer"
                  borderLeftWidth="3px"
                  borderLeftColor={selected ? `${color}.500` : "transparent"}
                  bg={selected ? `${color}.50` : "transparent"}
                  _hover={{ bg: selected ? `${color}.50` : "gray.50" }}
                  onClick={() => selectAccount(address.email)}
                >
                  <Circle
                    size="28px"
                    bg={`${color}.500`}
                    color="white"
                    fontSize="11px"
                    fontWeight="700"
                    flexShrink={0}
                  >
                    {initials(address)}
                  </Circle>

                  <Box minW={0} flex="1">
                    <Text
                      fontSize="sm"
                      fontWeight={selected ? "700" : "500"}
                      noOfLines={1}
                    >
                      {address.label || address.email.split("@")[0]}
                    </Text>
                    <Text fontSize="xs" color="gray.500" noOfLines={1}>
                      {address.email}
                    </Text>
                  </Box>

                  {draft && !selected && (
                    <Tooltip label="An unsent draft is waiting here">
                      <Box color="gray.400" fontSize="11px">
                        <EditIcon />
                      </Box>
                    </Tooltip>
                  )}

                  {unread > 0 && (
                    <Badge colorScheme="red" borderRadius="full" px={2}>
                      {unread}
                    </Badge>
                  )}
                </Flex>
              );
            })}
          </VStack>

          <Button
            mt={2}
            w="100%"
            size="xs"
            variant="ghost"
            leftIcon={<AddIcon />}
            onClick={addressModal.onOpen}
          >
            Add mailbox
          </Button>
        </Box>

        {/* ── The open mailbox ────────────────────────────────────────── */}
        <Box
          bg="white"
          borderRadius="lg"
          boxShadow="sm"
          p={4}
          flex="1"
          minW={0}
          w="100%"
          borderTopWidth="3px"
          borderTopColor={`${tone}.400`}
        >
          <Flex align="center" gap={2} mb={3}>
            <Circle
              size="26px"
              bg={`${tone}.500`}
              color="white"
              fontSize="10px"
              fontWeight="700"
            >
              {initials(current)}
            </Circle>
            <Text fontSize="sm" fontWeight="700" noOfLines={1}>
              {current?.label || account || "No mailbox"}
            </Text>
            <Text fontSize="xs" color="gray.500" noOfLines={1}>
              {account}
            </Text>
          </Flex>

          {thread ? (
            <Box>
              <Flex align="center" gap={2} mb={3}>
                <IconButton
                  size="sm"
                  variant="ghost"
                  aria-label="Back to the list"
                  icon={<ChevronLeftIcon />}
                  fontSize="xl"
                  onClick={() => setThread(null)}
                />
                <Heading size="sm" noOfLines={1}>
                  {thread.subject}
                </Heading>
              </Flex>

              <VStack align="stretch" spacing={3}>
                {thread.messages.map((message) => (
                  <Box
                    key={message.id}
                    borderWidth="1px"
                    borderColor={
                      message.direction === "inbound" ? "gray.200" : `${tone}.200`
                    }
                    bg={message.direction === "inbound" ? "white" : `${tone}.50`}
                    borderRadius="md"
                    p={3}
                  >
                    <Flex align="center" gap={2} mb={1} wrap="wrap">
                      <Badge
                        colorScheme={
                          message.direction === "inbound" ? "gray" : tone
                        }
                      >
                        {message.direction === "inbound" ? "received" : "sent"}
                      </Badge>
                      <Text fontSize="sm" fontWeight="600">
                        {message.fromName || message.fromEmail}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        → {message.to.join(", ")}
                      </Text>
                      <Box flex="1" />
                      <Text fontSize="xs" color="gray.500">
                        {formatDate(message.occurredAt)}
                      </Text>
                    </Flex>

                    <MessageBody message={message} />

                    <Button
                      mt={2}
                      size="xs"
                      variant="ghost"
                      colorScheme={tone}
                      leftIcon={<EmailIcon />}
                      onClick={() => startReply(message)}
                    >
                      Reply as {account.split("@")[0]}
                    </Button>
                  </Box>
                ))}
              </VStack>
            </Box>
          ) : (
            <Tabs
              colorScheme={tone}
              index={box === "inbox" ? 0 : 1}
              onChange={(index) => setBox(index === 0 ? "inbox" : "sent")}
            >
              <TabList>
                <Tab>
                  Inbox
                  {(unreadByAddress[account] || 0) > 0 && (
                    <Badge ml={2} colorScheme="red" borderRadius="full">
                      {unreadByAddress[account]}
                    </Badge>
                  )}
                </Tab>
                <Tab>Sent</Tab>
              </TabList>

              <HStack spacing={2} my={3}>
                <InputGroup size="sm" maxW="320px">
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder={`Search ${account || "this mailbox"}`}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                </InputGroup>

                <Box flex="1" />

                <Text fontSize="xs" color="gray.500">
                  {list ? `${list.total} message${list.total === 1 ? "" : "s"}` : ""}
                </Text>
              </HStack>

              <TabPanels>
                {[0, 1].map((panel) => (
                  <TabPanel key={panel} px={0} pt={0}>
                    {listLoading ? (
                      <VStack align="stretch" spacing={2}>
                        {[...Array(5)].map((_, index) => (
                          <Skeleton key={index} height="52px" borderRadius="md" />
                        ))}
                      </VStack>
                    ) : !list?.items.length ? (
                      <Text fontSize="sm" color="gray.500" py={6} textAlign="center">
                        {box === "inbox"
                          ? `Nothing received at ${account} yet. Inbound mail appears here once the Cloudflare Email Worker is bound to this address.`
                          : `Nothing sent from ${account} yet.`}
                      </Text>
                    ) : (
                      <VStack align="stretch" spacing={1}>
                        {list.items.map((message) => (
                          <Flex
                            key={message.id}
                            align="center"
                            gap={3}
                            p={2}
                            borderRadius="md"
                            cursor="pointer"
                            bg={message.isRead ? "transparent" : `${tone}.50`}
                            _hover={{ bg: "gray.100" }}
                            onClick={() => openThread(message.threadKey)}
                          >
                            <Box minW={0} flex="1">
                              <Text
                                fontSize="sm"
                                fontWeight={message.isRead ? "500" : "700"}
                                noOfLines={1}
                              >
                                {box === "inbox"
                                  ? message.fromName || message.fromEmail
                                  : message.to.join(", ")}
                              </Text>
                              <Text fontSize="sm" noOfLines={1}>
                                {message.subject}
                              </Text>
                              <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                {message.preview}
                              </Text>
                            </Box>
                            <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
                              {formatDate(message.occurredAt)}
                            </Text>
                          </Flex>
                        ))}
                      </VStack>
                    )}
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>
          )}

          {threadLoading && (
            <Flex justify="center" py={4}>
              <Spinner size="sm" />
            </Flex>
          )}
        </Box>

        {/* ── Compose ─────────────────────────────────────────────────── */}
        <Box
          id="compose-panel"
          bg="white"
          borderRadius="lg"
          boxShadow="sm"
          p={4}
          w={{ base: "100%", lg: "440px" }}
          flexShrink={0}
          borderTopWidth="3px"
          borderTopColor={mismatch ? "red.400" : `${tone}.400`}
        >
          <Flex align="center" mb={3}>
            <Heading size="sm">
              {form.replyToMessageId ? "Reply" : "New message"}
            </Heading>
            <Box flex="1" />
            {!isBlankForm(form) && (
              <Button size="xs" variant="ghost" onClick={resetForm}>
                Discard
              </Button>
            )}
          </Flex>

          <VStack align="stretch" spacing={3}>
            {/* The sending identity, stated rather than buried in a select. */}
            <Flex
              align="center"
              gap={2}
              px={3}
              py={2}
              borderRadius="md"
              borderWidth="1px"
              borderColor={mismatch ? "red.300" : `${tone}.200`}
              bg={mismatch ? "red.50" : `${tone}.50`}
            >
              <Circle
                size="28px"
                bg={`${colorFor(form.from)}.500`}
                color="white"
                fontSize="11px"
                fontWeight="700"
                flexShrink={0}
              >
                {initials(
                  addresses.find((address) => address.email === form.from)
                )}
              </Circle>
              <Box minW={0} flex="1">
                <Text fontSize="xs" color="gray.500">
                  Sending as
                </Text>
                <Text fontSize="sm" fontWeight="700" noOfLines={1}>
                  {form.from || "no address"}
                </Text>
              </Box>
              <Button
                size="xs"
                variant="ghost"
                onClick={() => setOverrideFrom((value) => !value)}
              >
                {overrideFrom ? "Done" : "Change"}
              </Button>
            </Flex>

            {overrideFrom && (
              <Select
                size="sm"
                value={form.from}
                onChange={(event) =>
                  setForm({ ...form, from: event.target.value })
                }
              >
                {addresses.map((address) => (
                  <option key={address.id} value={address.email}>
                    {address.label
                      ? `${address.label} <${address.email}>`
                      : address.email}
                  </option>
                ))}
              </Select>
            )}

            {mismatch && (
              <Alert status="warning" borderRadius="md" fontSize="xs" py={2}>
                <AlertIcon />
                <Box flex="1">
                  This goes out as {form.from}, while you are reading {account}.
                </Box>
                <Button
                  size="xs"
                  variant="link"
                  colorScheme="blue"
                  onClick={() => setForm({ ...form, from: account })}
                >
                  Use {account.split("@")[0]}
                </Button>
              </Alert>
            )}

            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                To — commas separate several
              </Text>
              <Input
                size="sm"
                placeholder="someone@example.com"
                value={form.to}
                onChange={(event) => setForm({ ...form, to: event.target.value })}
              />
            </Box>

            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                Cc — optional
              </Text>
              <Input
                size="sm"
                value={form.cc}
                onChange={(event) => setForm({ ...form, cc: event.target.value })}
              />
            </Box>

            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                Subject
              </Text>
              <Input
                size="sm"
                value={form.subject}
                onChange={(event) =>
                  setForm({ ...form, subject: event.target.value })
                }
              />
            </Box>

            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                Message
              </Text>
              <RichTextEditor
                value={form.html}
                onChange={(html) =>
                  setForm((current_) => ({ ...current_, html }))
                }
              />
            </Box>

            {form.quoted && (
              <Box borderWidth="1px" borderColor="gray.200" borderRadius="md">
                <Flex align="center" gap={2} px={2} py={1.5}>
                  <Text fontSize="xs" color="gray.500" noOfLines={1} flex="1">
                    {form.quoted.attribution}
                  </Text>
                  <Button
                    size="xs"
                    variant="ghost"
                    onClick={() => setShowQuoted((value) => !value)}
                  >
                    {showQuoted ? "Hide original" : "Show original"}
                  </Button>
                  <Tooltip label="Do not include the original in the reply">
                    <IconButton
                      size="xs"
                      variant="ghost"
                      colorScheme="red"
                      aria-label="Drop the quoted original"
                      icon={<DeleteIcon />}
                      onClick={() =>
                        setForm((current_) => ({ ...current_, quoted: null }))
                      }
                    />
                  </Tooltip>
                </Flex>

                {showQuoted && (
                  <Box
                    px={2}
                    pb={2}
                    borderTopWidth="1px"
                    borderColor="gray.100"
                    maxH="45vh"
                    overflowY="auto"
                  >
                    {form.quoted.html ? (
                      <HtmlBody html={form.quoted.html} />
                    ) : (
                      <Text
                        fontSize="sm"
                        whiteSpace="pre-wrap"
                        color="gray.600"
                        mt={2}
                      >
                        {form.quoted.text || "(empty)"}
                      </Text>
                    )}
                  </Box>
                )}
              </Box>
            )}

            <Button
              colorScheme={tone}
              leftIcon={<EmailIcon />}
              isDisabled={!canSend}
              onClick={confirmModal.onOpen}
            >
              Review and send
            </Button>

            <Text fontSize="xs" color="gray.400" textAlign="center">
              Drafts are kept per mailbox until they are sent or discarded.
            </Text>
          </VStack>
        </Box>
      </Flex>

      {/* ── One last look at who this is from ─────────────────────────── */}
      <Modal isOpen={confirmModal.isOpen} onClose={confirmModal.onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Send this message?</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={2} fontSize="sm">
              <Flex
                align="center"
                gap={2}
                px={3}
                py={2}
                borderRadius="md"
                bg={mismatch ? "red.50" : `${colorFor(form.from)}.50`}
              >
                <Circle
                  size="30px"
                  bg={`${colorFor(form.from)}.500`}
                  color="white"
                  fontSize="11px"
                  fontWeight="700"
                >
                  {initials(
                    addresses.find((address) => address.email === form.from)
                  )}
                </Circle>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    From
                  </Text>
                  <Text fontWeight="700">{form.from}</Text>
                </Box>
              </Flex>

              {mismatch && (
                <Alert status="warning" borderRadius="md" fontSize="xs" py={2}>
                  <AlertIcon />
                  Not the mailbox you are reading ({account}).
                </Alert>
              )}

              <Flex gap={2}>
                <Text color="gray.500" w="60px">
                  To
                </Text>
                <Text fontWeight="600">{form.to}</Text>
              </Flex>

              {form.cc.trim() && (
                <Flex gap={2}>
                  <Text color="gray.500" w="60px">
                    Cc
                  </Text>
                  <Text>{form.cc}</Text>
                </Flex>
              )}

              <Flex gap={2}>
                <Text color="gray.500" w="60px">
                  Subject
                </Text>
                <Text fontWeight="600">{form.subject}</Text>
              </Flex>

              <Box
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                p={2}
                maxH="200px"
                overflowY="auto"
                color="gray.600"
                whiteSpace="pre-wrap"
                fontSize="xs"
              >
                {htmlToText(form.html).slice(0, 600) || "(empty)"}
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button size="sm" variant="ghost" mr={2} onClick={confirmModal.onClose}>
              Keep editing
            </Button>
            <Button
              size="sm"
              colorScheme={mismatch ? "red" : tone}
              isLoading={sending}
              isDisabled={!canSend}
              onClick={send}
            >
              Send as {form.from}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── Mailboxes ─────────────────────────────────────────────────── */}
      <Modal isOpen={addressModal.isOpen} onClose={addressModal.onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Mailboxes</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={2} mb={4}>
              {addresses.map((address) => (
                <Flex key={address.id} align="center" gap={2}>
                  <Circle
                    size="28px"
                    bg={`${colorFor(address.email)}.500`}
                    color="white"
                    fontSize="11px"
                    fontWeight="700"
                  >
                    {initials(address)}
                  </Circle>
                  <Box flex="1" minW={0}>
                    <Text fontSize="sm" fontWeight="600">
                      {address.email}
                      {address.isDefault && (
                        <Badge ml={2} colorScheme="blue">
                          default
                        </Badge>
                      )}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {address.label || "no display name"}
                    </Text>
                  </Box>
                  {!address.isDefault && (
                    <Tooltip label="Remove from the list">
                      <IconButton
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        aria-label={`Remove ${address.email}`}
                        icon={<DeleteIcon />}
                        onClick={() => removeAddress(address)}
                      />
                    </Tooltip>
                  )}
                </Flex>
              ))}
            </VStack>

            <Divider mb={4} />

            <Text fontSize="sm" fontWeight="600" mb={2}>
              Add a mailbox
            </Text>
            <Text fontSize="xs" color="gray.500" mb={3}>
              Must be on storkplus.com — that is the domain Resend is verified
              for. Add it in Cloudflare Email Routing too if you want replies to
              it to reach this inbox.
            </Text>

            <HStack>
              <Input
                size="sm"
                placeholder="billing@storkplus.com"
                value={newAddress.email}
                onChange={(event) =>
                  setNewAddress({ ...newAddress, email: event.target.value })
                }
              />
              <Input
                size="sm"
                placeholder="Display name"
                value={newAddress.label}
                onChange={(event) =>
                  setNewAddress({ ...newAddress, label: event.target.value })
                }
              />
            </HStack>
          </ModalBody>
          <ModalFooter>
            <Button size="sm" variant="ghost" mr={2} onClick={addressModal.onClose}>
              Close
            </Button>
            <Button
              size="sm"
              colorScheme="blue"
              isLoading={addingAddress}
              isDisabled={!newAddress.email.trim()}
              onClick={addAddress}
            >
              Add
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AdminLayout>
  );
};

export default AdminMail;
